import { el, fmt } from './ui/app-api'
import { Direction, HudState } from '../types'
import { STARTING_LIVES } from '../constants/game-config'

/** In-game HUD: score/level/jelly chips, lives icon row, power ring, touch controls. */
export class HUD {
  readonly el: HTMLElement
  private devCompleteLevel: HTMLButtonElement | null = null
  private scoreV: HTMLElement
  private hiV: HTMLElement
  private levelV: HTMLElement
  private jellyV: HTMLElement
  private livesEl: HTMLElement
  private lastLives = -1
  private last = { score: -1, hi: -1, level: -1, jelly: -1, pearls: -1, ratio: -1, powered: false, warn: false }
  private power: HTMLElement
  private pearlsV: HTMLElement

  constructor(onDir: (d: Direction) => void, onPause: () => void, onDevCompleteLevel?: () => void) {
    this.el = el('div', { class: 'hud is-hidden', attrs: { id: 'hud' } })

    const scoreChip = this.chip('Score', '0')
    this.scoreV = scoreChip.value
    const hiChip = this.chip('Best', '0')
    this.hiV = hiChip.value
    const levelChip = this.chip('Level', '1', 'level')
    this.levelV = levelChip.value
    const jellyChip = this.chip('Jelly', '0', 'jelly')
    this.jellyV = jellyChip.value
    const pearlChip = this.chip('Pearls', '0')
    this.pearlsV = pearlChip.value

    this.livesEl = el('div', { class: 'lives' })
    const livesChip = el('div', { class: 'hud-chip' }, el('span', { class: 'label', text: 'Lives' }), this.livesEl)

    const left = el('div', { class: 'hud-cluster' }, scoreChip.root, hiChip.root)
    const right = el('div', { class: 'hud-cluster' }, levelChip.root, jellyChip.root, pearlChip.root, livesChip)
    const top = el('div', { class: 'hud-top' }, left, right)

    this.power = el(
      'div',
      { class: 'hud-power' },
      el('div', { class: 'core', text: 'POWER' }),
    )

    this.el.append(top, this.power, this.buildDpad(onDir), this.buildPauseBtn(onPause))
    if (onDevCompleteLevel) {
      this.devCompleteLevel = el('button', {
        class: 'dev-complete-level',
        text: 'DEV · COMPLETE LEVEL',
        attrs: { type: 'button', 'aria-label': 'DEV · COMPLETE LEVEL' },
      })
      this.devCompleteLevel.disabled = true
      this.devCompleteLevel.addEventListener('click', onDevCompleteLevel)
      this.el.append(this.devCompleteLevel)
    }
    this.renderLives(STARTING_LIVES)
  }

  private chip(label: string, value: string, extra = ''): { root: HTMLElement; value: HTMLElement } {
    const v = el('span', { class: 'value', text: value })
    const root = el('div', { class: `hud-chip ${extra}`.trim() }, el('span', { class: 'label', text: label }), v)
    return { root, value: v }
  }

  private buildDpad(onDir: (d: Direction) => void): HTMLElement {
    const pad = el('div', { class: 'touch-dpad' })
    const make = (cls: string, glyph: string, dir: Direction) => {
      const b = el('button', { class: `dpad-btn ${cls}`, text: glyph, attrs: { 'aria-label': dir } })
      const press = (e: Event) => {
        e.preventDefault()
        onDir(dir)
      }
      b.addEventListener('pointerdown', press)
      return b
    }
    pad.append(
      make('dpad-up', '▲', Direction.Up),
      make('dpad-down', '▼', Direction.Down),
      make('dpad-left', '◀', Direction.Left),
      make('dpad-right', '▶', Direction.Right),
    )
    return pad
  }

  private buildPauseBtn(onPause: () => void): HTMLElement {
    const wrap = el('div', { class: 'touch-pause' })
    const b = el('button', { class: 'bubble-btn bubble-btn--ghost bubble-btn--sm', text: '⏸' })
    b.addEventListener('click', onPause)
    wrap.append(b)
    return wrap
  }

  private renderLives(lives: number): void {
    if (lives === this.lastLives) return // lives change only on death — skip the 60Hz DOM churn
    this.lastLives = lives
    this.livesEl.replaceChildren()
    for (let i = 0; i < STARTING_LIVES; i++) {
      this.livesEl.append(el('span', { class: i < lives ? 'life' : 'life is-spent' }))
    }
  }

  update(h: HudState): void {
    // Skip unchanged writes — update() runs at 60Hz but values change rarely.
    const l = this.last
    if (h.score !== l.score) { this.scoreV.textContent = fmt(h.score); l.score = h.score }
    if (h.hiScore !== l.hi) { this.hiV.textContent = fmt(h.hiScore); l.hi = h.hiScore }
    if (h.level !== l.level) { this.levelV.textContent = String(h.level); l.level = h.level }
    if (h.jelly !== l.jelly) { this.jellyV.textContent = fmt(h.jelly); l.jelly = h.jelly }
    if (h.pearlsRemaining !== l.pearls) { this.pearlsV.textContent = String(h.pearlsRemaining); l.pearls = h.pearlsRemaining }
    this.renderLives(h.lives)
    if (h.powerRatio !== l.ratio) { this.power.style.setProperty('--ratio', String(h.powerRatio)); l.ratio = h.powerRatio }
    if (h.powered !== l.powered) { this.power.classList.toggle('is-active', h.powered); l.powered = h.powered }
    const warn = h.powered && h.powerRatio < 0.25
    if (warn !== l.warn) { this.power.classList.toggle('is-warning', warn); l.warn = warn }
  }

  /** Control state for the temporary production QA shortcut. */
  setDevCompleteLevelEnabled(enabled: boolean): void {
    if (this.devCompleteLevel) this.devCompleteLevel.disabled = !enabled
  }

  show(): void {
    this.el.classList.remove('is-hidden')
  }
  hide(): void {
    this.el.classList.add('is-hidden')
  }
}
